import Head from "next/head";
import { useRouter } from "next/router";
import { useTranslation } from "react-i18next";
import type { SitePageKey } from "./SiteHeader/siteNav";
import { SITE_OG_IMAGE_PATH, SITE_ORIGIN } from "./siteRoutes";

/** 머리 정보가 있는 사이트 화면 — 사이트 틀 페이지·로그인 계열·상태 화면 */
export type SiteMetaPage =
  | SitePageKey
  | "login"
  | "signup"
  | "changepassword"
  | "notFound"
  | "prepare";

/** 공유 미리보기 언어 표기(og:locale) — 그 밖의 언어는 한국어로 본다 */
const OG_LOCALE: Readonly<Record<string, string>> = {
  ko: "ko_KR",
  en: "en_US",
  ja: "ja_JP",
  km: "km_KH",
};

export interface SiteMetaProps {
  page: SiteMetaPage;
  /** 페이지 문구 대신 쓸 제목·설명(언론보도 상세처럼 내용마다 다른 페이지) */
  title?: string;
  description?: string;
  /** 검색에 내보내지 않을 화면(없는 주소·준비 중) — canonical 도 달지 않는다 */
  noindex?: boolean;
}

/**
 * 사이트 화면의 머리 정보(marketing-site-shell): 페이지별 제목·설명, canonical, 공유 미리보기(Open Graph·X 카드).
 * 공유 태그는 Next 가 `property` 로 중복을 걸러 주지 않으므로 `key` 를 단다 — 같은 key 는 나중 것이 남는다.
 */
export function SiteMeta({ page, title, description, noindex = false }: SiteMetaProps) {
  const { t, i18n } = useTranslation("landing");
  const router = useRouter();
  const pageTitle = title ?? t(`site.meta.${page}.title`);
  const pageDescription = description ?? t(`site.meta.${page}.description`);
  const path = (router.asPath || "/").split(/[?#]/)[0] || "/";
  const url = `${SITE_ORIGIN}${path}`;
  const image = `${SITE_ORIGIN}${SITE_OG_IMAGE_PATH}`;
  const locale = OG_LOCALE[(i18n.language || "ko").split("-")[0]] ?? OG_LOCALE.ko;

  return (
    <Head>
      <title key="title">{pageTitle}</title>
      <meta key="description" name="description" content={pageDescription} />
      {noindex ? (
        <meta key="robots" name="robots" content="noindex" />
      ) : (
        <link key="canonical" rel="canonical" href={url} />
      )}
      <meta key="og:type" property="og:type" content="website" />
      <meta key="og:site_name" property="og:site_name" content={t("site.meta.siteName")} />
      <meta key="og:title" property="og:title" content={pageTitle} />
      <meta key="og:description" property="og:description" content={pageDescription} />
      <meta key="og:url" property="og:url" content={url} />
      <meta key="og:locale" property="og:locale" content={locale} />
      <meta key="og:image" property="og:image" content={image} />
      <meta key="og:image:width" property="og:image:width" content="1200" />
      <meta key="og:image:height" property="og:image:height" content="630" />
      <meta key="og:image:alt" property="og:image:alt" content={t("site.meta.imageAlt")} />
      <meta key="twitter:card" name="twitter:card" content="summary_large_image" />
      <meta key="twitter:title" name="twitter:title" content={pageTitle} />
      <meta key="twitter:description" name="twitter:description" content={pageDescription} />
      <meta key="twitter:image" name="twitter:image" content={image} />
    </Head>
  );
}
